import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Provider } from 'react-redux'
import { StrictMode } from 'react'

import Main from './main'

// Configure @monaco-editor/react to load Monaco from local assets before any
// editor mounts (see common/helpers/monacoLoader). Must stay a top-level
// side-effect import so it runs at startup.
import 'common/helpers/monacoLoader'

import 'reactflow/dist/style.css'
import 'katex/dist/katex.min.css'
import './assets/index.css'

import reportWebVitals from './reportWebVitals'

import configureStore from './configureStore'
import { getBrowser } from 'common/helpers/getBrowser'
import { registerChunkLoadErrorRecovery } from 'common/helpers/chunkLoadRecovery'
import { captureExperienceOverride } from 'common/experience/experienceOverride'

// Set lang only if it is Safari or firefox
const setLang = () => {
  const browser = getBrowser()
  if (browser !== 'chrome') {
    const userLang = navigator.language || 'en'
    const langShort = userLang.split('-')[0]
    document.documentElement.lang = langShort
  }
}
setLang()
captureExperienceOverride()

/**
 * "ResizeObserver loop completed with undelivered notifications." is a benign
 * browser warning fired when an observer callback triggers another layout pass
 * within the same frame (e.g. opening/closing forms, wizards or resizable nodes
 * in the diagram). It does not break anything, but react-error-overlay promotes
 * it to a fatal runtime error overlay. We swallow only this specific message in
 * the capture phase so the overlay's handler never sees it.
 * This is dev-only behavior (the overlay isn't present in production builds), so it won't affect production.
 */
const RESIZE_OBSERVER_ERRORS = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.'
]

const isResizeObserverError = (message?: string) =>
  !!message && RESIZE_OBSERVER_ERRORS.some((msg) => message.includes(msg))

window.addEventListener(
  'error',
  (event) => {
    if (isResizeObserverError(event.message)) {
      event.stopImmediatePropagation()
      event.preventDefault()
    }
  },
  true
)

registerChunkLoadErrorRecovery()

export const store = configureStore()
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
})
// tanstask query devtools:
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import('@tanstack/query-core').QueryClient
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient

/**
 * TODO: lo volvemos a implementar cuando podamos identificar mejor los errores que lanzan pantalla en blanco de aquellos que no lo hacen
 */
// window.onerror = function (message, source, lineno, colno, error) {
//   console.error('Global error caught:', { message, source, lineno, colno, error })
//   const currentLocation = window.location.href
//   // To prevent false positives, only redirect if there is an error that is not a DOM event error
//   if (!isDOMEventError(error)) {
//     window.location.href = `/error?message=${message}&location=${currentLocation}`
//   }
// }

// const isDOMEventError = (error?: Error) => {
//   if (!error || !error.stack) {
//     return false
//   }
// window.onerror = function (message, source, lineno, colno, error) {
//   console.error('Global error caught:', { message, source, lineno, colno, error })
//   const currentLocation = window.location.href
// To prevent false positives, only redirect if there is an error that is not a DOM event error
// if (!isDOMEventError(error)) {
//   window.location.href = `/error?message=${message}&location=${currentLocation}`
// }
// }

//   // Check if the error was thrown in a DOM event handler
//   const domEventPattern = /at (HTML(?!UnknownElement)\w+Element|Document|Window)\./
//   return domEventPattern.test(error.stack)
// }

const container = document.getElementById('root')!
const root = createRoot(container)

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <HelmetProvider>
          <Main />
        </HelmetProvider>
      </Provider>
    </QueryClientProvider>
  </StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
